import React, { useState, useEffect } from 'react';
import { IoClose } from "react-icons/io5";
import { FiCalendar, FiClock, FiMapPin, FiUser, FiPackage, FiCreditCard, FiInfo, FiRepeat } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { contact } from '../../services/api';
import axios from 'axios';
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'

const TransferModal = ({ isOpen, onClose, partnerId, partnerCommissionRate, onSuccess }) => {
  const [formData, setFormData] = useState({
    pickUpDate: new Date(),
    dropOffDate: new Date(),
    pickupLocation: 'airport',
    dropoffLocation: 'airport',
    returnLocation: 'airport',
    otherPickupLocation: '',
    otherDropoffLocation: '',
    otherReturnLocation: '',
    isReturnTrip: false,
    pickupHours: '12',
    pickupMinutes: '00',
    dropoffHours: '12',
    dropoffMinutes: '00',
    name: '',
    surname: '',
    email: '',
    phone: '',
    persons: '',
    luggage: '',
    serviceType: '',
    carType: 'car',
    additionalInfo: '',
    paymentMethod: 'credit-card',
    partnerId: null,
    priceEstimate: 0
  });
  const [activeStep, setActiveStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [timeWarnings, setTimeWarnings] = useState({
    pickup: false,
    dropoff: false
  });
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [season, setSeason] = useState('');
  const [loadingPrice, setLoadingPrice] = useState(false);
  const locationOptions = [
    { value: "AthensAirport", label: "Athens Airport", icon: "✈️" },
    { value: "AthensCityHotels", label: "Athens City/Hotels", icon: "🏙️" },
    { value: "PiraeusPort", label: "Piraeus Port", icon: "🚢" },
    { value: "PiraeusCity", label: "Piraeus City", icon: "🏙️" },
    { value: "LavrioPort", label: "Lavrio Port", icon: "🚢" },
    { value: "LavrioCity", label: "Lavrio City", icon: "🏙️" },
    { value: "other", label: "Other Location", icon: "📍" },
  ];
  
    useEffect(() => {
      // Wait for DOM to be ready
        if (activeStep === 2) {
        const phoneInput = document.getElementsByClassName( "PhoneInputInput")[0];
        if (phoneInput) {
          phoneInput.className =
            "PhoneInputInput rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm";
        }
      }
    }, [activeStep]);

  // Use effect to set partner ID from props
  useEffect(() => {
    if (partnerId) {
      setFormData(prev => ({
        ...prev,
        partnerId
      }));
    }
  }, [partnerId]);

  // Handle toggle for return trip
  const handleReturnTripToggle = () => {
    setFormData(prev => {
      const isReturnActive = !prev.isReturnTrip;
      
      // If toggling on, default the return location to the pickup location
      // This creates the proper flow: pickup → dropoff → pickup (return)
      return {
        ...prev,
        isReturnTrip: isReturnActive,
        serviceType: isReturnActive ? 'round-trip' : 'one-way',
        // When activating return trip, set the return location to the pickup location
        returnLocation: isReturnActive ? prev.pickupLocation : prev.returnLocation,
        otherReturnLocation: isReturnActive && prev.pickupLocation === 'other' ? 
          prev.otherPickupLocation : prev.otherReturnLocation
      };
    });
  }

  // Get price estimate when relevant fields change
  useEffect(() => {
    const getPriceEstimate = async () => {
      // Only get price if both locations and vehicle type are set
      if (
        formData.pickupLocation &&
        formData.dropoffLocation &&
        formData.carType
      ) {
        try {
          setLoadingPrice(true);
          const pickup =
            formData.pickupLocation === "other"
              ? "other"
              : formData.pickupLocation;
          const dropoff =
            formData.dropoffLocation === "other"
              ? "other"
              : formData.dropoffLocation;

          // Don't fetch price for custom locations
          if (pickup === "other" || dropoff === "other") {
            setPriceEstimate(null);
            setSeason("");
            setLoadingPrice(false);
            return;
          }

          const date = formData.pickUpDate;
          const serviceType = formData.serviceType;
          const carType = formData.carType || "car";

          // For round trips, the returnLocation should be the original pickup location
          // This follows the flow: pickup → dropoff → pickup (as return)
          let returnLocation = null;
          if (serviceType === "round-trip") {
            returnLocation = formData.returnLocation === "other" 
              ? "other" 
              : formData.returnLocation;
          }

          // Make the API request with all necessary parameters
          const response = await axios.get(
            `https://blackleader-server.create-site.gr/api/contact/transfer-price`,
            {
              params: {
                pickup,
                dropoff,
                returnLocation,
                date,
                serviceType,
                carType,
              },
            }
          );

          if (response.data) {
            // Set season regardless of whether price is decided
            setSeason(response.data.season);

            // Check if price exists and is > 0
            if (response.data.price && response.data.price > 0) {
              setPriceEstimate(response.data.price);
              
              // Update form data with price estimate
              setFormData(prev => ({
                ...prev,
                priceEstimate: response.data.price
              }));
            } else {
              // No valid price
              setPriceEstimate(null);
            }
          } else {
            setPriceEstimate(null);
            setSeason("");
          }
        } catch (error) {
          console.error("Error fetching price estimate:", error);
          setPriceEstimate(null);
          setSeason("");
        } finally {
          setLoadingPrice(false);
        }
      } else {
        // Reset price if data is incomplete
        setPriceEstimate(null);
        setSeason("");
      }
    };

    // Only run if partnerId exists (for partner modal)
    if (partnerId) {
      getPriceEstimate();
    }
  }, [
    formData.pickupLocation,
    formData.dropoffLocation,
    formData.returnLocation,
    formData.pickUpDate,
    formData.carType,
    formData.serviceType,
    partnerId
  ]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle date changes
  const handleDateChange = (date, type) => {
    const isPickup = type === "pickUpDate";
    const stateKey = isPickup ? "pickup" : "dropoff";
    
    // Check if both date and time are set
    const hasHours = formData[isPickup ? "pickupHours" : "dropoffHours"];
    const hasMinutes = formData[isPickup ? "pickupMinutes" : "dropoffMinutes"];
    const hasTime = hasHours && hasMinutes;

    // Show warning if time is not selected
    setTimeWarnings(prev => ({
      ...prev,
      [stateKey]: !hasTime
    }));

    setFormData(prev => ({
      ...prev,
      [type]: date
    }));
  };

  // Navigate between steps
  const nextStep = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setActiveStep(prev => prev + 1);
      setIsAnimating(false);
    }, 300);
  };

  const prevStep = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setActiveStep(prev => prev - 1);
      setIsAnimating(false);
    }, 300);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    // Combine date and time
    const pickupDateTime = new Date(formData.pickUpDate);
    pickupDateTime.setHours(parseInt(formData.pickupHours), parseInt(formData.pickupMinutes));
    
    const dropoffDateTime = new Date(formData.dropOffDate);
    dropoffDateTime.setHours(parseInt(formData.dropoffHours), parseInt(formData.dropoffMinutes));

    try {
      // Prepare the data
      const transferData = {
        ...formData,
        pickUpDate: pickupDateTime,
        dropOffDate: dropoffDateTime,
        pickupLocation: formData.pickupLocation,
        dropoffLocation: formData.dropoffLocation
      };

      // If this is a partner submission, add the partner ID
      if (partnerId) {
        transferData.partnerId = partnerId;
      }

      await contact.submitTransferRequest(transferData);
      
      // If onSuccess callback is provided, call it
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error submitting transfer request:', error);
      setSubmitError('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle escape key and scroll lock
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle outside click
  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (    
    <div 
      onClick={handleOutsideClick}
      className="fixed inset-0 bg-black/70 z-50 flex justify-center items-center p-4 backdrop-blur-sm animate-fadeIn"
    >
      <div className="bg-white rounded-xl w-full max-w-4xl shadow-2xl relative animate-slideUp overflow-hidden border border-gray-100">
        {/* Progress indicator */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gray-100">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 transition-all duration-500 ease-in-out"
            style={{ width: `${(activeStep / 3) * 100}%` }}
          ></div>
        </div>

        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 text-transparent bg-clip-text">Private Transfer Request</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100"
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          <form onSubmit={handleSubmit}>
            <div className={`transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
              {/* Step 1: Personal Information */}
              {activeStep === 1 && (
                <div className="space-y-6 animate-fadeIn">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Personal Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Full Name</label>
                      <div className="flex space-x-3">
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="First name"
                          className="flex-1 border-0 bg-gray-50 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none transition"
                          required
                        />
                        <input
                          type="text"
                          name="surname"
                          value={formData.surname}
                          onChange={handleChange}
                          placeholder="Last name"
                          className="flex-1 border-0 bg-gray-50 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none transition"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Contact Details</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email address"
                        className="w-full border-0 bg-gray-50 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none transition"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Phone Number</label>
                      <div className="relative">
                        {/* <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Phone number with country code"
                          className="w-full border-0 bg-gray-50 rounded-lg p-3 pl-10 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none transition"
                          required
                        /> */}
                                <PhoneInput
                                  international
                                  defaultCountry="GR"
                                  value={formData.phone}
                                  onChange={(value) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    phone: value,
                                  }));
                                  handleChange({
                                    target: {
                                    name: "phone",
                                    value: value,
                                    },
                                  });
                                  }}
                                  placeholder="Phone number with country code"
                                  autoComplete="on"
                                />
                        <span className="absolute left-3 top-3 text-gray-400">📞</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Number of People</label>
                        <div className="relative">
                          <FiUser className="absolute left-3 top-3.5 text-gray-400" />
                          <input
                            type="number"
                            name="persons"
                            value={formData.persons}
                            onChange={handleChange}
                            min="1"
                            placeholder="Passengers"
                            className="w-full border-0 bg-gray-50 rounded-lg p-3 pl-10 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none transition"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Luggage</label>
                        <div className="relative">
                          <FiPackage className="absolute left-3 top-3.5 text-gray-400" />
                          <input
                            type="number"
                            name="luggage"
                            value={formData.luggage}
                            onChange={handleChange}
                            min="0"
                            placeholder="Bags"
                            className="w-full border-0 bg-gray-50 rounded-lg p-3 pl-10 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none transition"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Trip Details */}
              {activeStep === 2 && (
                <div className="space-y-6 animate-fadeIn">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Trip Details</h3>
                  
                  {/* Pick-up Details */}
                  <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                    <h4 className="font-medium text-indigo-700 mb-4">Pick-up Information</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Date & Time</label>
                        <div className="flex space-x-2">
                          <div className="relative flex-1">
                            <FiCalendar className="absolute left-3 top-3.5 text-gray-400" />
                            <DatePicker
                              selected={formData.pickUpDate}
                              onChange={(date) => handleDateChange(date, 'pickUpDate')}
                              className="w-full border border-blue-100 rounded-lg p-3 pl-10 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                              minDate={new Date()}
                            />
                          </div>
                          <div className="flex space-x-1 items-center">
                            <div className="relative">
                              <FiClock className="absolute left-3 top-3.5 text-gray-400" />
                              <select 
                                name="pickupHours"
                                value={formData.pickupHours}
                                onChange={handleChange}
                                className="border border-blue-100 rounded-lg p-3 pl-10 pr-3 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                              >
                                {[...Array(24)].map((_, i) => (
                                  <option key={i} value={i.toString().padStart(2, '0')}>{i.toString().padStart(2, '0')}</option>
                                ))}
                              </select>
                            </div>
                            <span className="text-lg font-medium text-gray-600">:</span>
                            <select
                              name="pickupMinutes"
                              value={formData.pickupMinutes}
                              onChange={handleChange}
                              className="border border-blue-100 rounded-lg p-3 pr-3 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                            >
                              {['00', '15', '30', '45'].map(min => (
                                <option key={min} value={min}>{min}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {timeWarnings.pickup && (
                          <p className="text-sm text-red-500 mt-2">Please select a time for pick-up.</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Location</label>                        <div className="relative">
                          <FiMapPin className="absolute left-3 top-3.5 text-gray-400" />
                          <select
                            name="pickupLocation"
                            value={formData.pickupLocation}
                            onChange={handleChange}
                            className="w-full border border-blue-100 rounded-lg p-3 pl-10 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                          >
                            {locationOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.icon} {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {formData.pickupLocation === 'other' && (
                          <input
                            type="text"
                            name="otherPickupLocation"
                            value={formData.otherPickupLocation}
                            onChange={handleChange}
                            placeholder="Specify location"
                            className="w-full mt-2 border border-blue-100 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Drop-off Details */}
                  <div className="p-5 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl">
                    <h4 className="font-medium text-purple-700 mb-4">Drop-off Information</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Date & Time</label>
                        <div className="flex space-x-2">
                          <div className="relative flex-1">
                            <FiCalendar className="absolute left-3 top-3.5 text-gray-400" />
                            <DatePicker
                              selected={formData.dropOffDate}
                              onChange={(date) => handleDateChange(date, 'dropOffDate')}
                              className="w-full border border-purple-100 rounded-lg p-3 pl-10 text-gray-800 focus:ring-2 focus:ring-purple-500 outline-none"
                              minDate={formData.pickUpDate}
                            />
                          </div>
                          <div className="flex space-x-1 items-center">
                            <div className="relative">
                              <FiClock className="absolute left-3 top-3.5 text-gray-400" />
                              <select 
                                name="dropoffHours"
                                value={formData.dropoffHours}
                                onChange={handleChange}
                                className="border border-purple-100 rounded-lg p-3 pl-10 pr-3 text-gray-800 focus:ring-2 focus:ring-purple-500 outline-none appearance-none"
                              >
                                {[...Array(24)].map((_, i) => (
                                  <option key={i} value={i.toString().padStart(2, '0')}>{i.toString().padStart(2, '0')}</option>
                                ))}
                              </select>
                            </div>
                            <span className="text-lg font-medium text-gray-600">:</span>
                            <select
                              name="dropoffMinutes"
                              value={formData.dropoffMinutes}
                              onChange={handleChange}
                              className="border border-purple-100 rounded-lg p-3 pr-3 text-gray-800 focus:ring-2 focus:ring-purple-500 outline-none appearance-none"
                            >
                              {['00', '15', '30', '45'].map(min => (
                                <option key={min} value={min}>{min}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {timeWarnings.dropoff && (
                          <p className="text-sm text-red-500 mt-2">Please select a time for drop-off.</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Location</label>                        <div className="relative">
                          <FiMapPin className="absolute left-3 top-3.5 text-gray-400" />
                          <select
                            name="dropoffLocation"
                            value={formData.dropoffLocation}
                            onChange={handleChange}
                            className="w-full border border-purple-100 rounded-lg p-3 pl-10 text-gray-800 focus:ring-2 focus:ring-purple-500 outline-none appearance-none"
                          >
                            {locationOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.icon} {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {formData.dropoffLocation === 'other' && (
                          <input
                            type="text"
                            name="otherDropoffLocation"
                            value={formData.otherDropoffLocation}
                            onChange={handleChange}
                            placeholder="Specify location"
                            className="w-full mt-2 border border-purple-100 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-purple-500 outline-none"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Return Trip Toggle */}
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-purple-700 flex items-center">
                        <FiRepeat className="mr-2" />
                        Return Trip
                      </h4>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="isReturnTrip"
                          checked={formData.isReturnTrip}
                          onChange={handleReturnTripToggle}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-purple-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>

                    {/* Return Location - Only shown when isReturnTrip is true */}
                    {formData.isReturnTrip && (
                      <div className="mt-4 border-t border-purple-100 pt-4">
                        <h5 className="font-medium text-pink-700 mb-3">Return Location</h5>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">
                            Where would you like to be returned to after your destination?
                          </label>
                          <div className="relative">
                            <FiMapPin className="absolute left-3 top-3.5 text-gray-400" />
                            <select
                              name="returnLocation"
                              value={formData.returnLocation}
                              onChange={handleChange}
                              className="w-full border border-pink-100 rounded-lg p-3 pl-10 text-gray-800 focus:ring-2 focus:ring-pink-500 outline-none appearance-none"
                            >
                              {locationOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.icon} {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          {formData.returnLocation === 'other' && (
                            <input
                              type="text"
                              name="otherReturnLocation"
                              value={formData.otherReturnLocation}
                              onChange={handleChange}
                              placeholder="Specify return location"
                              className="w-full mt-2 border border-pink-100 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-pink-500 outline-none"
                            />
                          )}

                          {/* Explain the return trip flow to the user */}
                          <div className="mt-3 bg-pink-50 p-3 rounded-lg text-sm text-pink-800">
                            <div className="flex items-center mb-2">
                              <FiInfo className="flex-shrink-0 mr-2 text-pink-600" />
                              <span className="font-medium">Trip Route:</span>
                            </div>
                            <div className="pl-5 space-y-1">
                              <p className="flex items-center">
                                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-800 flex-shrink-0 flex items-center justify-center text-xs mr-2">1</span>
                                <span>
                                  Going from <strong>{formData.pickupLocation === 'other' ? formData.otherPickupLocation || 'Custom pickup' : 
                                  formData.pickupLocation}</strong>
                                </span>
                              </p>
                              <p className="flex items-center">
                                <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-800 flex-shrink-0 flex items-center justify-center text-xs mr-2">2</span>
                                <span>
                                  To <strong>{formData.dropoffLocation === 'other' ? formData.otherDropoffLocation || 'Custom destination' : 
                                  locationOptions.find(opt => opt.value === formData.dropoffLocation)?.label}</strong>
                                </span>
                              </p>
                              <p className="flex items-center">
                                <span className="w-5 h-5 rounded-full bg-pink-100 text-pink-800 flex-shrink-0 flex items-center justify-center text-xs mr-2">3</span>
                                <span>
                                  Then returning to <strong>{formData.returnLocation === 'other' ? formData.otherReturnLocation || 'Custom return location' : 
                                  locationOptions.find(opt => opt.value === formData.returnLocation)?.label}</strong>
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Step 3: Additional Details */}
              {activeStep === 3 && (
                <div className="space-y-6 animate-fadeIn">
                  {submitError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                      {submitError}
                    </div>
                  )}
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Service Details</h3>
                    {/* Partner Price and Commission Section */}
                  {partnerId && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl mb-6 shadow-sm animate-fadeIn">
                      <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                        <span className="mr-2 text-lg">💰</span>
                        Price Estimate
                      </h4>
                      
                      {loadingPrice ? (
                        <div className="flex items-center justify-center p-6 animate-pulse">
                          <div className="w-5 h-5 rounded-full bg-blue-600 mr-1 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-5 h-5 rounded-full bg-indigo-600 mr-1 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-5 h-5 rounded-full bg-purple-600 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <div className="flex items-center">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                                  season === 'high' ? 'bg-red-100 text-red-600' : 
                                  season === 'medium' ? 'bg-yellow-100 text-yellow-600' : 
                                  'bg-green-100 text-green-600'
                                }`}>
                                  {season === 'high' ? '🔥' : season === 'medium' ? '☀️' : '🍃'}
                                </div>
                                <div>
                                  <p className="text-xs uppercase tracking-wide text-gray-500">Season</p>
                                  <p className="font-medium text-gray-800">
                                    {season === 'high' ? 'High Season' :
                                    season === 'medium' ? 'Medium Season' :
                                    season === 'low' ? 'Low Season' : 'Not available'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <div className="flex items-center">
                                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3">
                                  💶
                                </div>
                                <div>
                                  <p className="text-xs uppercase tracking-wide text-gray-500">Total Price</p>
                                  <p className="font-medium text-gray-800">
                                    {priceEstimate ? `€${priceEstimate.toFixed(2)}` : 'To be decided'}
                                    {formData.serviceType === 'round-trip' && priceEstimate && (
                                      <span className="text-xs ml-2 text-blue-600">
                                        (Includes return trip)
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <div className="flex items-center">
                                <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-3">
                                  💎
                                </div>
                                <div>
                                  <p className="text-xs uppercase tracking-wide text-gray-500">Your Commission</p>
                                  <p className="font-medium text-green-600">
                                    {priceEstimate ? 
                                      `€${((priceEstimate * partnerCommissionRate) / 100).toFixed(2)} (${partnerCommissionRate}%)` : 
                                      'To be decided'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Display info about return trip pricing if applicable */}
                          {formData.isReturnTrip && priceEstimate && (
                            <div className="mt-3 bg-blue-50 p-3 rounded-lg text-sm text-blue-800 flex items-start">
                              <FiInfo className="flex-shrink-0 mt-0.5 mr-2 text-blue-600" />
                              <p>
                                This price includes both your initial trip and the return journey. 
                                {formData.pickupLocation !== formData.returnLocation && 
                                  " Note that your return location differs from your pickup point."}
                              </p>
                            </div>
                          )}

                          {!priceEstimate && formData.pickupLocation === 'other' || formData.dropoffLocation === 'other' && (
                            <p className="text-sm text-gray-600 mt-3 bg-blue-50 p-2 rounded">
                              For custom locations, the price will be determined after submission.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Service Type</label>
                      <select
                        name="serviceType"
                        value={formData.serviceType}
                        onChange={(e) => {
                          handleChange(e);
                          // Update isReturnTrip when service type is changed manually
                          if (e.target.value === 'round-trip' && !formData.isReturnTrip) {
                            setFormData(prev => ({
                              ...prev,
                              isReturnTrip: true
                            }));
                          } else if (e.target.value === 'one-way' && formData.isReturnTrip) {
                            setFormData(prev => ({
                              ...prev,
                              isReturnTrip: false
                            }));
                          }
                        }}
                        className="w-full border-0 bg-gray-50 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition"
                        required
                      >
                        <option value="">Select service type</option>
                        <option value="one-way">One-way Transfer</option>
                        <option value="round-trip">Return Transfer</option>
                      </select>
                      {formData.isReturnTrip && (
                        <p className="text-xs text-indigo-600 mt-1">
                          Return trip to: {formData.returnLocation === 'other' ? formData.otherReturnLocation || 'Custom location' : locationOptions.find(opt => opt.value === formData.returnLocation)?.label}
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Vehicle Preference</label>
                      <select
                        name="carType"
                        value={formData.carType}
                        onChange={handleChange}
                        className="w-full border-0 bg-gray-50 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition"
                      >
                        <option value="">Select vehicle (optional)</option>
                        <option value="car">Standard Sedan (1-3 passengers)</option>
                        <option value="minivan"> Minivan (4-7 passengers)</option>
                        <option value="van"> Luxury Van (8-15 passengers)</option>
                        <option value="minibus"> Minibus (16-30 passengers)</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Additional Information</label>
                    <div className="relative">
                      <FiInfo className="absolute left-3 top-3.5 text-gray-400" />
                      <textarea
                        name="additionalInfo"
                        value={formData.additionalInfo}
                        onChange={handleChange}
                        placeholder="Special requests, flight details, etc."
                        className="w-full border-0 bg-gray-50 rounded-lg p-3 pl-10 h-24 text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition resize-none"
                      ></textarea>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Payment Method</label>
                    <div className="grid grid-cols-2 gap-3">
                      {['Cash', 'Credit Card'].map(method => (
                        <label key={method} className="relative cursor-pointer">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={method.toLowerCase().replace(' ', '-')}
                            checked={formData.paymentMethod === method.toLowerCase().replace(' ', '-') }
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <div className={`flex items-center justify-center p-3 rounded-lg border-2 transition-all ${
                            formData.paymentMethod === method.toLowerCase().replace(' ', '-')
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}>
                            <FiCreditCard className={`mr-2 ${
                              formData.paymentMethod === method.toLowerCase().replace(' ', '-')
                                ? 'text-indigo-500'
                                : 'text-gray-400'
                            }`} />
                            <span className={formData.paymentMethod === method.toLowerCase().replace(' ', '-') ? 'font-medium text-indigo-700' : 'text-gray-700'}>
                              {method}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  

                </div>
              )}
            </div>
          </form>
        </div>        {/* Footer with buttons */}
        <div className="p-6 border-t border-gray-100 flex justify-between items-center bg-gray-50">
          <button 
            onClick={activeStep > 1 ? prevStep : onClose}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-300 flex items-center ${
              activeStep > 1 
                ? 'text-indigo-600 hover:bg-indigo-50 hover:scale-105' 
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {activeStep > 1 ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Previous
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Cancel
              </>
            )}
          </button>
          
          <div className="flex items-center">
            <div className="flex space-x-4 mr-6">
              {[1, 2, 3].map(step => (
                <div key={step} className="flex flex-col items-center">
                  <div 
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      activeStep === step 
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 scale-125 shadow-md' 
                        : activeStep > step 
                          ? 'bg-indigo-400' 
                          : 'bg-gray-300'
                    }`}
                  ></div>
                  <span className={`text-xs mt-1 font-medium ${
                    activeStep === step ? 'text-indigo-600' : 'text-gray-400'
                  }`}>
                    {step === 1 ? 'Info' : step === 2 ? 'Trip' : 'Confirm'}
                  </span>
                </div>
              ))}
            </div>
            
            {activeStep < 3 ? (
              <button 
                type="button"
                onClick={nextStep}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2.5 rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 flex items-center"
              >
                Continue
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            ) : (
              <button 
                type="submit"
                onClick={handleSubmit}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-10 py-2.5 rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Request
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .animate-pulse {
          animation: pulse 1.5s infinite;
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-bounce {
          animation: bounce 0.8s infinite;
        }
      `}</style>
    </div>
  );
};

export default TransferModal;